import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchFAQs, fetchCategories, markFAQHelpful } from '../../redux/supportSlice';
import ToastService from '../../core/notifications/ToastService';

export const FAQPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { faqs, categories, isLoading, error } = useSelector((state: RootState) => state.support);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<string | false>(false);

  const loadFAQs = useCallback(() => {
    dispatch(fetchFAQs({
      category: selectedCategory || undefined,
      search: searchQuery || undefined
    }));
  }, [selectedCategory, searchQuery, dispatch]);

  useEffect(() => {
    dispatch(fetchCategories());
    loadFAQs();
  }, [dispatch, loadFAQs]);

  const handleSearch = () => {
    loadFAQs();
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    dispatch(fetchFAQs({
      category: category || undefined,
      search: searchQuery || undefined
    }));
  };

  const handleMarkHelpful = async (faqId: string, helpful: boolean) => {
    try {
      await dispatch(markFAQHelpful({ faqId, helpful })).unwrap();
      ToastService.success('Thank you for your feedback!');
      loadFAQs();
    } catch (err: any) {
      ToastService.error(err || 'Failed to submit feedback');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton
          onClick={() => navigate('/support')}
          sx={{ display: { xs: 'flex', sm: 'none' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/support')}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
            Find quick answers to common questions
          </Typography>
        </Box>
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button onClick={handleSearch} sx={{ backgroundColor: '#14B8A6', color: 'white' }}>
                    Search
                  </Button>
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* Categories */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label="All"
          onClick={() => handleCategorySelect('')}
          color={selectedCategory === '' ? 'primary' : 'default'}
          sx={{ cursor: 'pointer' }}
        />
        {categories.faq_categories.map((category) => (
          <Chip
            key={category}
            label={category}
            onClick={() => handleCategorySelect(category)}
            color={selectedCategory === category ? 'primary' : 'default'}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* FAQs */}
      {isLoading && faqs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : faqs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No FAQs found
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Try adjusting your search or category filter
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {faqs.map((faq) => (
            <Accordion
              key={faq.id}
              expanded={expandedFAQ === faq.id}
              onChange={() => setExpandedFAQ(expandedFAQ === faq.id ? false : faq.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                    {faq.question}
                  </Typography>
                  <Chip label={faq.category} size="small" sx={{ display: { xs: 'none', sm: 'flex' } }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: '#666', mb: 3, whiteSpace: 'pre-line' }}>
                  {faq.answer}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 2, borderTop: '1px solid #eee', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Was this helpful?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<ThumbUpIcon />}
                      onClick={() => handleMarkHelpful(faq.id, true)}
                      variant="outlined"
                    >
                      Yes ({faq.helpful_count})
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ThumbDownIcon />}
                      onClick={() => handleMarkHelpful(faq.id, false)}
                      variant="outlined"
                    >
                      No ({faq.not_helpful_count})
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Still need help */}
      <Card sx={{ mt: 4, backgroundColor: '#f9f9f9' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Still need help?
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Can't find what you're looking for? Create a support ticket and our team will assist you.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/support/tickets')}
            sx={{ backgroundColor: '#14B8A6' }}
          >
            Create Support Ticket
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default FAQPage;
